// src/components/common/CloudflareTurnstile.jsx
import React, { useRef, useEffect, useState } from 'react';
import apiClient from '../../api/apiClient';

const CloudflareTurnstile = ({ 
  siteKey, 
  onVerify, 
  theme = 'auto', 
  size = 'normal' 
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [actualSiteKey, setActualSiteKey] = useState(siteKey || process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY || '');

  useEffect(() => {
    // First try to get siteKey from the backend if not provided directly
    const fetchSiteKey = async () => {
      if (!actualSiteKey) {
        try {
          const response = await apiClient.get('/config');
          if (response.data && response.data.turnstileSiteKey) {
            setActualSiteKey(response.data.turnstileSiteKey);
            logToServer('Fetched siteKey from backend', { siteKey: response.data.turnstileSiteKey });
          }
        } catch (error) {
          logToServer('Error fetching siteKey from backend', { error: error.message });
        }
      }
    };

    fetchSiteKey();
    
    // Log when component mounts
    logToServer('CloudflareTurnstile mounted', { 
      providedSiteKey: siteKey,
      envSiteKey: process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY,
      actualSiteKey
    });

    // Load the Cloudflare Turnstile script
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.onload = () => {
      logToServer('Turnstile script loaded');
    };
    document.body.appendChild(script);

    // Clean up on component unmount
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      document.body.removeChild(script);
    };
  }, [siteKey, actualSiteKey]);

  useEffect(() => {
    // Initialize the widget when the script is loaded and we have a siteKey
    const interval = setInterval(() => {
      if (window.turnstile && containerRef.current && actualSiteKey) {
        clearInterval(interval);
        
        logToServer('Rendering Turnstile widget', { siteKey: actualSiteKey });
        
        // Render the widget
        try {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: actualSiteKey,
            callback: (token) => {
              if (onVerify) {
                onVerify(token);
              }
              
              // No need to log every successful token verification
              if (process.env.REACT_APP_NODE_ENV === 'development') {
                logToServer('Turnstile token received', { 
                  tokenPrefix: token.substring(0, 10)
                });
              }
            },
            "error-callback": (error) => {
              // Always log errors, but with forceLog=true
              logToServer('Turnstile error', { error, siteKey: actualSiteKey }, true);
            },
            theme: theme,
            size: size,
          });
        } catch (error) {
          // Always log rendering errors, with forceLog=true
          logToServer('Error rendering Turnstile widget', { 
            error: error.message,
            siteKey: actualSiteKey
          }, true);
        }
      }
    }, 100);

    return () => clearInterval(interval);
  }, [actualSiteKey, onVerify, theme, size]);

  // Helper function to log to server - only logs in development or on errors
  const logToServer = (message, data = {}, forceLog = false) => {
    // Only log in development environment or if forceLog is true (for errors)
    if (process.env.REACT_APP_NODE_ENV !== 'development' && !forceLog) {
      return;
    }
    
    try {
      fetch(process.env.REACT_APP_API_URL + '/debug/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message,
          data,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    } catch (e) {}
  };

  return <div ref={containerRef} />;
};

export default CloudflareTurnstile;